import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminFAQDeviationsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        content: '',
        display_order: 0,
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/faq-deviations');
            if (response.data.success) {
                setRows(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching deviations:', error);
            toast.error('Failed to load deviations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row) => {
        setEditingRow(row);
        setFormData({
            content: row.content,
            display_order: row.display_order,
            is_active: row.is_active === 1 || row.is_active === true
        });
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingRow(null);
        setFormData({
            content: '',
            display_order: rows.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingRow) {
                await axios.put(`/api/admin/faq-deviations/${editingRow.id}`, formData);
                toast.success('Deviation updated successfully');
            } else {
                await axios.post('/api/admin/faq-deviations', formData);
                toast.success('Deviation added successfully');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving deviation:', error);
            toast.error('Failed to save deviation');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/faq-deviations/${deleteConfirm.id}`);
            toast.success('Deviation deleted successfully');
            setDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting deviation:', error);
            toast.error('Failed to delete deviation');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">FAQ Deviations</h1>
                    <p className="text-slate-500 mt-1">Manage the deviation points shown on the FAQ page</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg text-sm"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add Deviation Point
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12">Loading...</div>
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <ListBulletIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-800 mb-2">No deviations found</h3>
                    <p className="text-slate-500">Add points to show them in the FAQ Deviations section.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden text-sans">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                <tr>
                                    <th className="p-4 w-16 text-center">Order</th>
                                    <th className="p-4">Content</th>
                                    <th className="p-4 text-center">Status</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4 text-center text-slate-500 font-medium">{row.display_order}</td>
                                        <td className="p-4 text-slate-800 font-medium">{row.content}</td>
                                        <td className="p-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {row.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => handleEdit(row)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDelete(row.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="px-8 py-6 border-b border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-800 font-sans">{editingRow ? 'Edit Deviation' : 'Add Deviation'}</h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto font-sans">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Deviation Content</label>
                                    <textarea
                                        required
                                        rows="3"
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. If ABS ≥ 1 EMI, rate can be waived up to 1%."
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                        <input
                                            type="number"
                                            value={formData.display_order}
                                            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 pt-8">
                                        <input
                                            type="checkbox"
                                            id="isActive"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                            className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                                        />
                                        <label htmlFor="isActive" className="text-sm font-semibold text-slate-700">Active</label>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all font-sans"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 font-sans shadow-lg shadow-primary-200"
                                >
                                    {submitting ? 'Saving...' : 'Save Deviation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Deviation"
                message="Are you sure you want to delete this deviation point? This action cannot be undone."
            />
        </div>
    );
};

export default AdminFAQDeviationsPage;
